import { cn } from "@/lib/cn";
import type { CSSProperties } from "react";
import { Kicker } from "./kicker";
import { StatusDot, type StatusDotVariant } from "./status-dot";

export interface HealthComponent {
  /** Component display name (e.g. "Backend API", "Redis") */
  name: string;
  /** Status variant */
  variant: StatusDotVariant;
  /** Human-readable state label (e.g. "OK", "ERR", "WARN") */
  state: string;
}

export interface HealthPanelProps {
  /** Environment label shown in the kicker header */
  env?: "DEV" | "PROD";
  /** List of components and their health status */
  components: HealthComponent[] | null | undefined;
  className?: string;
  style?: CSSProperties;
}

/**
 * Side panel listing infrastructure components with their health status.
 * Uses iris-health CSS classes and StatusDot for status indicators.
 * The kicker color adapts to the environment (cyan for DEV, purple for PROD).
 */
export function HealthPanel({ env = "DEV", components, className, style }: HealthPanelProps) {
  const isDev = env === "DEV";
  const color = isDev ? "var(--iris-neon-cyan)" : "var(--iris-neon-purple)";
  const glow = isDev ? "var(--iris-glow-cyan)" : "var(--iris-glow-purple)";

  return (
    <div className={cn("iris-health", className)} style={style}>
      <Kicker style={{ marginBottom: 12 }}>
        <span style={{ color, textShadow: glow }}>{env}</span>
        <span style={{ color, textShadow: glow, marginLeft: 6 }}>HEALTH</span>
      </Kicker>
      {!components || components.length === 0 ? (
        // IR-1771: nothing reported is Unknown (neutral), never blank or healthy.
        <div className="iris-health__row">
          <StatusDot variant="idle" size={8} />
          <span className="iris-health__state" style={{ color: "var(--iris-fg-3)" }}>
            Unknown
          </span>
        </div>
      ) : (
        components.map((c) => (
          <div key={c.name} className="iris-health__row">
            <StatusDot variant={c.variant} size={8} />
            <span className="iris-health__name">{c.name}</span>
            <span className={cn("iris-health__state", `iris-health__state--${c.variant}`)}>
              {c.state}
            </span>
          </div>
        ))
      )}
    </div>
  );
}
